import { useEffect, useRef, useState } from 'react';
import type { Track } from 'livekit-client';
import {
  ALL_GUEST_PERMISSIONS,
  GUEST_PERMISSION_LABELS,
  type Huddle,
  type HuddleGuest,
} from '@avslack/shared';
import { decideHuddleGuest, removeHuddleGuest, setHuddleGuestPermissions } from '../lib/api';
import { playHandRaiseChime } from '../lib/sound';
import { useUsers } from '../data/users';

/**
 * A HIVAS VENDEGEI (H2) - a telefonos sav mintajara kulon sav, nem csempe.
 *
 *  - A VARAKOZO: neve, Beengedes / Elutasitas. Barki a hivasban donthet; ha
 *    ketten egyszerre kattintanak, az elso dont.
 *  - A BENT LEVO: kis kep (ha kameraja van), beszed-jelzes, a jogai -
 *    kattintasra kapcsolhatok, hivas kozben is -, es Kivetel.
 *
 * Uj varakozora egy hangjelzes szol: a hivasban ulok eppen beszelnek, egy
 * csendes sav konnyen elkerulne a figyelmet.
 */
export function HuddleGuests({
  huddle,
  speakingIds,
  cameraTracks,
}: {
  huddle: Huddle;
  speakingIds: string[];
  cameraTracks: { identity: string; track: Track }[];
}) {
  const guests = huddle.guests ?? [];
  const waitingIds = guests.filter((g) => g.state === 'waiting').map((g) => g.id);
  const seen = useRef(new Set<string>());

  useEffect(() => {
    const fresh = waitingIds.filter((id) => !seen.current.has(id));
    fresh.forEach((id) => seen.current.add(id));
    if (fresh.length > 0) playHandRaiseChime();
  }, [waitingIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (guests.length === 0) return null;

  return (
    <ul className="flex shrink-0 flex-wrap gap-2" aria-label="Vendégek">
      {guests.map((guest) => (
        <GuestRow
          key={guest.id}
          huddleId={huddle.id}
          guest={guest}
          speaking={speakingIds.includes(guest.identity)}
          camera={cameraTracks.find((item) => item.identity === guest.identity)?.track ?? null}
        />
      ))}
    </ul>
  );
}

function GuestRow({
  huddleId,
  guest,
  speaking,
  camera,
}: {
  huddleId: string;
  guest: HuddleGuest;
  speaking: boolean;
  camera: Track | null;
}) {
  const { nameOf } = useUsers();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLDivElement | null>(null);
  const waiting = guest.state === 'waiting';
  const cameraLive = camera !== null && !camera.isMuted;

  useEffect(() => {
    if (!camera || !cameraLive) return;
    const element = camera.attach();
    element.className = 'h-full w-full object-cover';
    videoRef.current?.replaceChildren(element);
    return () => element.remove();
  }, [camera, cameraLive]);

  const run = (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    void action()
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'Nem sikerült.'),
      )
      .finally(() => setBusy(false));
  };

  const togglePermission = (permission: (typeof ALL_GUEST_PERMISSIONS)[number]) => {
    const next = guest.permissions.includes(permission)
      ? guest.permissions.filter((p) => p !== permission)
      : [...guest.permissions, permission];
    run(() => setHuddleGuestPermissions(huddleId, guest.id, next));
  };

  return (
    <li
      className={[
        'flex min-w-0 items-center gap-3 rounded-card border px-3 py-2 text-sm',
        waiting
          ? 'animate-pulse border-alert bg-alert-soft'
          : speaking
            ? 'border-accent bg-surface-raised ring-2 ring-accent'
            : 'border-border-soft bg-surface-raised',
      ].join(' ')}
    >
      <div className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-full bg-surface-hover text-xs font-semibold">
        {cameraLive ? (
          <div ref={videoRef} className="h-full w-full" />
        ) : (
          guest.name
            .split(/\s+/)
            .map((part) => part[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()
        )}
      </div>

      <div className="min-w-0">
        <p className="truncate font-semibold">
          {guest.name}{' '}
          <span className="rounded-pill bg-surface-hover px-1.5 py-0.5 text-2xs font-medium text-ink-muted">
            vendég
          </span>
        </p>
        {waiting ? (
          <p className="text-xs text-alert">Várakozik, hogy beengedjék</p>
        ) : (
          <div className="mt-1 flex flex-wrap gap-1" aria-label="A vendég jogai">
            {ALL_GUEST_PERMISSIONS.map((permission) => {
              const on = guest.permissions.includes(permission);
              return (
                <button
                  key={permission}
                  type="button"
                  disabled={busy}
                  aria-pressed={on}
                  title={on ? 'Jog elvétele' : 'Jog megadása'}
                  onClick={() => togglePermission(permission)}
                  className={[
                    'rounded-pill border px-1.5 py-0.5 text-2xs font-medium',
                    on
                      ? 'border-accent bg-accent-soft text-accent'
                      : 'border-border-soft text-ink-faint line-through',
                  ].join(' ')}
                >
                  {GUEST_PERMISSION_LABELS[permission]}
                </button>
              );
            })}
          </div>
        )}
        {!waiting && guest.decidedBy && (
          <p className="mt-0.5 text-2xs text-ink-faint">Beengedte: {nameOf(guest.decidedBy)}</p>
        )}
        {error && (
          <p role="alert" className="text-2xs text-danger">
            {error}
          </p>
        )}
      </div>

      <div className="ml-auto flex shrink-0 gap-1.5">
        {waiting ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => decideHuddleGuest(huddleId, guest.id, true))}
              className="rounded-pill bg-accent px-3 py-1 text-xs font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-50"
            >
              Beengedés
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => decideHuddleGuest(huddleId, guest.id, false))}
              className="rounded-pill border border-border px-3 py-1 text-xs font-semibold hover:bg-surface-hover disabled:opacity-50"
            >
              Elutasítás
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => removeHuddleGuest(huddleId, guest.id))}
            className="rounded-pill px-2 py-1 text-xs text-danger hover:bg-danger-soft disabled:opacity-50"
          >
            Kivétel
          </button>
        )}
      </div>
    </li>
  );
}
